import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {
	Box,
	Breadcrumbs,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Grid,
	Link,
	Paper,
	Typography,
} from "@mui/material";
import { fetchNotificationCenterItem } from "actions/notificationCenterActions";
import dayjs from "dayjs";
import { selectNotificationCenter } from "features/notificationCenter";
import { useAppSelector, useAuth } from "hooks/hooks";
import React, { useEffect, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate, useParams } from "react-router";
import remarkEmoji from "remark-emoji";
import remarkGfm from "remark-gfm";
import styles from "./NotificationDetail.module.scss";

const NotificationDetail: React.FC = () => {
	const { fileName } = useParams<{ fileName: string }>();
	const navigate = useNavigate();
	const { dispatch, auth } = useAuth();

	const { notifications, isLoading } = useAppSelector(selectNotificationCenter);

	const notification = useMemo(() => {
		const fullFileName = `${fileName}.md`;
		return notifications.find((n) => n.file === fullFileName);
	}, [notifications, fileName]);

	useEffect(() => {
		if (notification && !notification.content) {
			dispatch(fetchNotificationCenterItem({ auth, fileName: notification.file }));
		}
	}, [dispatch, notification, auth]);

	const handleBackClick = () => {
		navigate("/notifications");
	};

	const getTypeColor = (type: string) => {
		switch (type) {
			case "notification":
				return "primary";
			case "newsletter":
				return "info";
			case "recommendation":
				return "warning";
			case "securityalert":
				return "error";
			case "releasenote":
				return "success";
			default:
				return "default";
		}
	};

	const getTypeLabel = (type: string) => {
		switch (type) {
			case "notification":
				return "Notification";
			case "newsletter":
				return "Newsletter";
			case "recommendation":
				return "Recommendation";
			case "securityalert":
				return "Security Alert";
			case "releasenote":
				return "Release Note";
			default:
				return type;
		}
	};

	const extractContentFromMarkdown = (content: any) => {
		// Ensure content is a string and handle edge cases
		if (!content) {
			return "";
		}

		let contentString: string;

		if (typeof content === "string") {
			contentString = content;
		} else if (Array.isArray(content)) {
			// If it's an array of strings (lines), join them with newlines
			// Preserve empty lines and ensure proper markdown spacing
			contentString = content
				.map((line: any) => (typeof line === "string" ? line : String(line)))
				.join("\n");
		} else if (typeof content === "object") {
			// If it's an object, try to stringify it first
			contentString = JSON.stringify(content);

			// Then try to parse it as JSON string and check if it's an array
			try {
				const parsed = JSON.parse(contentString);
				if (Array.isArray(parsed)) {
					// If parsed content is an array, join with newlines
					contentString = parsed
						.map((line: any) => (typeof line === "string" ? line : String(line)))
						.join("\n");
				} else if (typeof parsed === "string") {
					contentString = parsed;
				}
			} catch (error) {
				console.log("Content is not a JSON string, using as-is");
			}
		} else {
			// Convert to string as fallback
			contentString = String(content);
		}

		// First, convert escaped newlines to actual newlines
		contentString = contentString.replace(/\\n/g, "\n");

		// Fix escaped markdown syntax (e.g., \*\* becomes **)
		// Handle double-escaped markdown (\\\*\\\* becomes **)
		contentString = contentString.replace(/\\\\\*/g, "*");
		contentString = contentString.replace(/\\\*/g, "*");
		contentString = contentString.replace(/\\\\_/g, "_");
		contentString = contentString.replace(/\\_/g, "_");
		contentString = contentString.replace(/\\\\`/g, "`");
		contentString = contentString.replace(/\\`/g, "`");

		// Debug: Log the content before processing
		if (process.env.NODE_ENV === "development") {
			console.log("Before processing:", JSON.stringify(contentString));
		}

		// Handle potential character encoding issues from Data Lake
		const cleanContent = contentString
			.replace(/â€™/g, "'")
			.replace(/â€œ/g, '"')
			.replace(/â€/g, '"')
			.replace(/â€"/g, "—")
			.replace(/ðŸš€/g, "🚀")
			.replace(/ðŸ"¥/g, "🔥")
			.replace(/âœ¨/g, "✨")
			.replace(/ðŸ'¡/g, "💡")
			.replace(/âš¡/g, "⚡")
			.replace(/âœ"/g, "✅")
			.replace(/â­/g, "⭐");
		// Note: Removed aggressive character filtering that was breaking markdown

		// Debug: Log the content after processing
		if (process.env.NODE_ENV === "development") {
			console.log("After processing:", JSON.stringify(cleanContent));
		}

		return cleanContent.trim();
	};

	if (isLoading || !notification) {
		return (
			<Grid
				container
				justifyContent="center"
				alignItems="center"
				style={{ minHeight: "400px" }}
			>
				<CircularProgress />
			</Grid>
		);
	}

	if (!notification) {
		return (
			<Grid container spacing={3} className={styles.container}>
				<Grid item xs={12}>
					<Paper elevation={1} sx={{ p: 4, textAlign: "center" }}>
						<NotificationsIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
						<Typography variant="h6" color="text.secondary">
							Notification not found
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							The notification you&apos;re looking for doesn&apos;t exist or has been
							removed.
						</Typography>
						<Button
							variant="contained"
							onClick={handleBackClick}
							startIcon={<ArrowBackIcon />}
						>
							Back to Notifications
						</Button>
					</Paper>
				</Grid>
			</Grid>
		);
	}

	return (
		<Grid container spacing={3} className={styles.container}>
			{/* Breadcrumbs */}
			<Grid item xs={12}>
				<Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
					<Link
						color="inherit"
						href="/notifications"
						onClick={(e) => {
							e.preventDefault();
							handleBackClick();
						}}
						sx={{ cursor: "pointer" }}
					>
						Notifications
					</Link>
					<Typography color="text.primary">{notification.title}</Typography>
				</Breadcrumbs>
			</Grid>

			{/* Back Button */}
			<Grid item xs={12}>
				<Button
					variant="outlined"
					onClick={handleBackClick}
					startIcon={<ArrowBackIcon />}
					sx={{ mb: 2 }}
				>
					Back to Notifications
				</Button>
			</Grid>

			{/* Notification Header */}
			<Grid item xs={12}>
				<Card className={styles.headerCard}>
					<CardContent>
						<Box
							display="flex"
							justifyContent="space-between"
							alignItems="flex-start"
							mb={2}
						>
							<Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
								{notification.title}
							</Typography>
							<Chip
								label={getTypeLabel(notification.type)}
								color={getTypeColor(notification.type) as any}
								size="medium"
								className={styles.typeChip}
							/>
						</Box>

						<Box display="flex" gap={2} alignItems="center" mb={2}>
							<Typography variant="body2" color="text.secondary">
								Published: {dayjs(notification.date).format("MMMM D, YYYY")}
							</Typography>
						</Box>

						<Box display="flex" gap={1} flexWrap="wrap">
							<Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
								Products:
							</Typography>
							{notification.products.map((product) => (
								<Chip
									key={product}
									label={product}
									variant="outlined"
									size="small"
									color="default"
								/>
							))}
						</Box>
					</CardContent>
				</Card>
			</Grid>

			{/* Notification Content */}
			<Grid item xs={12}>
				<Card className={styles.contentCard}>
					<CardContent>
						{notification.content ? (
							<div className={styles.markdownContent}>
								<ReactMarkdown
									remarkPlugins={[remarkGfm, remarkEmoji]}
									components={{
										h1: ({ children }) => (
											<Typography
												variant="h4"
												component="h1"
												gutterBottom
												sx={{ fontWeight: 600 }}
											>
												{children}
											</Typography>
										),
										h2: ({ children }) => (
											<Typography
												variant="h5"
												component="h2"
												gutterBottom
												sx={{ fontWeight: 600, mt: 3 }}
											>
												{children}
											</Typography>
										),
										h3: ({ children }) => (
											<Typography
												variant="h6"
												component="h3"
												gutterBottom
												sx={{ fontWeight: 600, mt: 2 }}
											>
												{children}
											</Typography>
										),
										p: ({ children }) => (
											<Typography variant="body1" paragraph>
												{children}
											</Typography>
										),
										strong: ({ children }) => <strong>{children}</strong>,
										em: ({ children }) => <em>{children}</em>,
										blockquote: ({ children }) => (
											<Box
												sx={{
													borderLeft: "4px solid",
													borderColor: "primary.main",
													pl: 2,
													ml: 0,
													py: 1,
													backgroundColor: "grey.50",
													fontStyle: "italic",
												}}
											>
												{children}
											</Box>
										),
										ul: ({ children }) => (
											<Box component="ul" sx={{ pl: 3, mb: 1.5, mt: 1 }}>
												{children}
											</Box>
										),
										ol: ({ children }) => (
											<Box component="ol" sx={{ pl: 3, mb: 1.5, mt: 1 }}>
												{children}
											</Box>
										),
										li: ({ children }) => (
											<Box
												component="li"
												sx={{
													mb: 0.75,
													"& > p": { margin: 0, display: "inline" },
													"& > ul, & > ol": { mt: 0.5, mb: 0 },
												}}
											>
												{children}
											</Box>
										),
										hr: () => (
											<Box
												sx={{
													borderBottom: "1px solid",
													borderColor: "divider",
													my: 3,
												}}
											/>
										),
									}}
								>
									{extractContentFromMarkdown(notification.content)}
								</ReactMarkdown>
							</div>
						) : (
							<Box
								display="flex"
								justifyContent="center"
								alignItems="center"
								minHeight="200px"
							>
								<CircularProgress />
								<Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
									Loading content...
								</Typography>
							</Box>
						)}
					</CardContent>
				</Card>
			</Grid>
		</Grid>
	);
};

export default NotificationDetail;
